import React, { useEffect, useMemo, useState } from "react";
import { Alert, Button, Card, Form, Spinner } from "react-bootstrap";
import {
  FaArrowLeft,
  FaBookOpen,
  FaCalendarAlt,
  FaCheckCircle,
  FaCross,
  FaDharmachakra,
  FaExclamationTriangle,
  FaMosque,
  FaOm,
  FaPrayingHands,
  FaRegClock,
  FaSave,
  FaYinYang,
} from "react-icons/fa";
import BottomNavbar from "../components/BottomNavbar";
import {
  fetchPresensiIbadah,
  simpanPresensiIbadah,
} from "../services/presensiIbadahService";

const iconByReligion = {
  islam: FaMosque,
  kristen: FaCross,
  katolik: FaCross,
  hindu: FaOm,
  buddha: FaDharmachakra,
  khonghucu: FaYinYang,
};

const statusOptions = [
  { key: "sudah", label: "Sudah", color: "success", icon: FaCheckCircle },
  { key: "belum", label: "Belum", color: "primary", icon: FaRegClock },
  {
    key: "kendala",
    label: "Ada Kendala",
    color: "warning",
    icon: FaExclamationTriangle,
  },
];

const today = () => new Date().toISOString().slice(0, 10);

function PresensiIbadah() {
  const [tanggal, setTanggal] = useState(today);
  const [options, setOptions] = useState({});
  const [agama, setAgama] = useState(
    () => localStorage.getItem("agama_ibadah") || ""
  );
  const [records, setRecords] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState("");
  const [message, setMessage] = useState(null);

  const loadData = async (selectedAgama = agama) => {
    setLoading(true);
    const result = await fetchPresensiIbadah(tanggal, selectedAgama);
    if (result.success) {
      setOptions(result.data.options || {});
      const nextRecords = {};
      (result.data.data || []).forEach((record) => {
        nextRecords[record.ibadah] = record;
      });
      setRecords(nextRecords);
    } else {
      setMessage({ type: "danger", text: result.message });
    }
    setLoading(false);
  };

  useEffect(() => {
    loadData();
    // Load the selected date and religion once on page entry.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tanggal]);

  const religions = useMemo(
    () => Object.entries(options),
    [options]
  );
  const selectedReligion = options[agama];
  const ReligionIcon = iconByReligion[agama] || FaPrayingHands;

  const chooseReligion = (key) => {
    setAgama(key);
    localStorage.setItem("agama_ibadah", key);
    loadData(key);
  };

  const chooseStatus = (ibadah, status) => {
    setRecords((previous) => ({
      ...previous,
      [ibadah]: { ...(previous[ibadah] || {}), ibadah, status },
    }));
  };

  const setNote = (ibadah, keterangan) => {
    setRecords((previous) => ({
      ...previous,
      [ibadah]: { ...(previous[ibadah] || {}), ibadah, keterangan },
    }));
  };

  const saveItem = async (item) => {
    const record = records[item.key];
    if (!record?.status) {
      setMessage({ type: "danger", text: `Pilih status untuk ${item.label}.` });
      return;
    }

    setSaving(item.key);
    const result = await simpanPresensiIbadah({
      tanggal,
      agama,
      ibadah: item.key,
      status: record.status,
      keterangan: record.keterangan || null,
    });
    setSaving("");

    if (result.success) {
      setRecords((previous) => ({
        ...previous,
        [item.key]: result.data.data,
      }));
      setMessage({ type: "success", text: `${item.label} berhasil disimpan.` });
    } else {
      setMessage({ type: "danger", text: result.message });
    }
  };

  return (
    <div className="container py-3" style={{ minHeight: "100vh", paddingBottom: 90 }}>
      <div className="d-flex align-items-center mb-3">
        <Button variant="link" className="p-0 text-dark" onClick={() => window.history.back()}>
          <FaArrowLeft />
        </Button>
        <h5 className="mb-0 ms-3 fw-bold">Presensi Ibadah</h5>
      </div>

      {message && (
        <Alert variant={message.type} dismissible onClose={() => setMessage(null)}>
          {message.text}
        </Alert>
      )}

      <Card className="border-0 shadow-sm rounded-4 mb-3">
        <Card.Body>
          <div className="d-flex align-items-center mb-2">
            <FaBookOpen className="text-primary me-2" />
            <div>
              <div className="fw-bold">Catatan Ibadah</div>
              <small className="text-muted">Pilih agama dan catat pelaksanaan ibadah hari ini.</small>
            </div>
          </div>
          <Form.Label className="small fw-semibold mt-2">Tanggal</Form.Label>
          <div className="input-group mb-3">
            <span className="input-group-text"><FaCalendarAlt /></span>
            <Form.Control type="date" value={tanggal} onChange={(event) => setTanggal(event.target.value)} />
          </div>
          <Form.Label className="small fw-semibold">Agama / Kepercayaan</Form.Label>
          <div className="row g-2">
            {religions.map(([key, item]) => {
              const Icon = iconByReligion[key] || FaPrayingHands;
              return (
                <div className="col-6 col-md-4" key={key}>
                  <button
                    type="button"
                    className={`btn w-100 h-100 py-3 ${agama === key ? "btn-primary" : "btn-outline-primary"}`}
                    onClick={() => chooseReligion(key)}
                  >
                    <Icon size={22} className="d-block mx-auto mb-1" />
                    <span>{item.label}</span>
                  </button>
                </div>
              );
            })}
          </div>
        </Card.Body>
      </Card>

      {loading ? (
        <div className="text-center py-5"><Spinner animation="border" variant="primary" /></div>
      ) : !agama ? (
        <Card className="border-0 shadow-sm rounded-4 text-center text-muted p-4">
          Pilih agama/kepercayaan terlebih dahulu untuk melihat daftar ibadah.
        </Card>
      ) : (
        <>
          <Card className="border-0 shadow-sm rounded-4 mb-3">
            <Card.Body className="d-flex align-items-center">
              <ReligionIcon size={32} className="text-primary me-3" />
              <div>
                <h6 className="fw-bold mb-1">Ibadah {selectedReligion?.label}</h6>
                <small className="text-muted">Pilih status untuk setiap kegiatan ibadah.</small>
              </div>
            </Card.Body>
          </Card>

          {selectedReligion?.items.map((item) => {
            const record = records[item.key] || {};
            return (
              <Card className="border-0 shadow-sm rounded-4 mb-3" key={item.key}>
                <Card.Body>
                  <div className="d-flex justify-content-between align-items-start mb-3">
                    <div>
                      <div className="fw-bold">{item.label}</div>
                      <small className="text-muted">Catat pelaksanaan ibadah</small>
                    </div>
                    {record.status && <span className={`badge text-bg-${record.status === "kendala" ? "warning" : record.status === "sudah" ? "success" : "primary"}`}>{statusOptions.find((status) => status.key === record.status)?.label}</span>}
                  </div>
                  <div className="d-flex gap-2 flex-wrap">
                    {statusOptions.map((status) => {
                      const Icon = status.icon;
                      return (
                        <Button
                          key={status.key}
                          size="sm"
                          variant={record.status === status.key ? status.color : `outline-${status.color}`}
                          onClick={() => chooseStatus(item.key, status.key)}
                        >
                          <Icon className="me-1" /> {status.label}
                        </Button>
                      );
                    })}
                  </div>
                  {record.status === "kendala" && (
                    <Form.Control
                      as="textarea"
                      rows={2}
                      className="mt-3"
                      placeholder="Keterangan kendala (opsional)"
                      value={record.keterangan || ""}
                      onChange={(event) => setNote(item.key, event.target.value)}
                    />
                  )}
                  <Button
                    className="w-100 mt-3"
                    variant="primary"
                    disabled={saving === item.key}
                    onClick={() => saveItem(item)}
                  >
                    {saving === item.key ? <Spinner size="sm" animation="border" /> : <><FaSave className="me-2" /> Simpan Presensi</>}
                  </Button>
                </Card.Body>
              </Card>
            );
          })}
        </>
      )}

      <BottomNavbar />
    </div>
  );
}

export default PresensiIbadah;
